import React, { useContext, useEffect, useState, useRef } from 'react';
import { AppContext } from '~/providers/AppProvider';
import styled from 'styled-components';
import { useRouter } from 'next/router';
import { SearchMapWithClusters } from '@boatsetter-backup/js-component-lib';
import Supercluster from 'supercluster';
import { parseBoatsForClustering, parseClustersForMap } from '~/utils/objects';
import { getCenterFromRectangle } from '~/utils/map';
import moment from 'moment';
import { getCultureToken } from '~/utils/langUtils';
import getConfig from 'next/config';
import { slugifyToURL } from '../boats/utils';
const { publicRuntimeConfig } = getConfig();

interface BoatMapProps {
  hoveredResultId: any;
  setHoveredResultId: any;
  parsedBoats: Object[];
  footerInView?: boolean;
  mapFullScreen: boolean;
  showMap: boolean;
  map: any;
  setMap: any;
}
export default function BoatMap({
  hoveredResultId,
  setHoveredResultId,
  parsedBoats,
  footerInView,
  mapFullScreen,
  showMap,
  map,
  setMap,
}: BoatMapProps) {
  const appCtx = useContext(AppContext);
  const router = useRouter();

  const [zoom, setZoom] = useState(12);
  const [dragged, setDragged] = useState(false);
  const [dragSearch, setDragSearch] = useState(true);
  const [dragSearchVisible, setDragSearchVisible] = useState(true);
  const [openMarker, setOpenMarker] = useState(null);
  const [dragButtonVisible, setDragButtonVisible] = useState(false);
  const [lastMapObj, setLastMapObj] = useState(null);
  const [mapBounds, setMapBounds] = useState(null);
  const [boatsClusters, setBoatClusters] = useState([]);
  const [canDoDragEnd, setCanDoDragEnd] = useState(true);

  const isInitialRender = useRef(true);

  useEffect(() => {
    if (isInitialRender && isInitialRender.current) {
      isInitialRender.current = false;
    }
  }, []);

  useEffect(() => {
    if (!showMap || !map) return;

    onDragEnd(map, true);
  }, [showMap, map]);

  useEffect(() => {
    if (!showMap) setCounter(1);
  }, [showMap]);

  useEffect(() => {
    const parsedSearchResults = parseBoatsForClustering(parsedBoats);
    const clusterUtil = new Supercluster({
      radius: 400,
      maxZoom: 20,
    });

    clusterUtil.load(parsedSearchResults);

    // const clusters = clusterUtil.getClusters(
    //     [
    //         parseFloat(String(router.query.longitudeNe)),
    //         parseFloat(String(router.query.latitudeNe)),
    //         parseFloat(String(router.query.longitudeSw)),
    //         parseFloat(String(router.query.latitudeSw)),
    //     ],
    //     zoom
    // );

    const clusters = clusterUtil.getClusters([180, -88, 180, 88], zoom);
    const parsedClusters = parseClustersForMap(clusters, clusterUtil);

    setBoatClusters(parsedClusters);
  }, [parsedBoats, zoom]);

  useEffect(() => {
    if (!dragButtonVisible) {
      if (lastMapObj) {
        onDragEnd(lastMapObj, true);
      }
    }
  }, [dragButtonVisible]);

  const mapCentre = getCenterFromRectangle({
    sw_lat: parseFloat(String(router.query.latitudeSw)),
    ne_lat: parseFloat(String(router.query.latitudeNe)),
    sw_lng: parseFloat(String(router.query.longitudeSw)),
    ne_lng: parseFloat(String(router.query.longitudeNe)),
  });

  const DRAG_TOLERANCE = 300; // in meters
  const [counter, setCounter] = useState(0);

  const onDragEnd = (map, forceSearch) => {
    if (!dragSearch && forceSearch === undefined) {
      setDragButtonVisible(true);
      return;
    }
    if (!map) {
      return;
    }

    // max value 2, make sure we use bouding params from query for first 2 calls, and then we use actualy map coordinates to fetch result
    if (counter < 2) {
      map?.fitBounds(
        new window.google.maps.LatLngBounds(
          new window.google.maps.LatLng(
            parseFloat(String(router.query.latitudeSw)),
            parseFloat(String(router.query.longitudeSw))
          ),
          new window.google.maps.LatLng(
            parseFloat(String(router.query.latitudeNe)),
            parseFloat(String(router.query.longitudeNe))
          )
        ),
        0
      );
      setCounter(counter + 1);
      return;
    }

    if (!canDoDragEnd) {
      // console.log('DRAG END STOPPED !!! ---');

      return;
    }

    setCanDoDragEnd(false);
    setTimeout(() => {
      setCanDoDragEnd(true);
    }, 500);

    // console.log('onDragEnd has been called');

    setLastMapObj(map);
    setZoom(map.getZoom());
    setMapBounds(map.getBounds());

    const bounds = map.getBounds();

    const ne = bounds.getNorthEast();
    const sw = bounds.getSouthWest();
    const neLat = ne.lat();
    const neLng = ne.lng();
    const swLat = sw.lat();
    const swLng = sw.lng();

    const boundingBox = {
      sw_lat: swLat,
      sw_lng: swLng,
      ne_lat: neLat,
      ne_lng: neLng,
    };

    const currentQueries = router.query;
    delete currentQueries.pageNumber;

    // appCtx.actions.setLoading(true);

    const mapCenter = bounds.getCenter();

    const currentCenter = new window.google.maps.LatLng(mapCentre.lat, mapCentre.lon);
    const distance = window.google.maps.geometry.spherical.computeDistanceBetween(mapCenter, currentCenter);

    if (distance < DRAG_TOLERANCE && !forceSearch) return;

    router.replace({
      pathname: router.pathname,
      query: {
        ...currentQueries,
        latitudeSw: boundingBox.sw_lat,
        longitudeSw: boundingBox.sw_lng,
        latitudeNe: boundingBox.ne_lat,
        longitudeNe: boundingBox.ne_lng,
        action: 'map',
      },
    });
    setCounter(counter + 1);
  };

  const getBoatDetailUrl = (boatAlias, boatId, locationId, locationName) => {
    const hasDate =
      router.query.availableFrom === undefined
        ? ``
        : `&availableFrom=${moment(router.query.availableFrom).format('YYYY-MM-DD')}`;

    const hasPassengers =
      router.query.passengerCount === undefined ? `` : `&passengerCount=${router.query.passengerCount}`;

    const currencyUrlPart =
      appCtx.state.defaultCurrency === appCtx.state.currency ? '' : `currency=${appCtx.state.currency}`;

    const hasQueries = hasDate || hasPassengers || currencyUrlPart ? '?' : '';

    return `${publicRuntimeConfig.ASSETS_PREFIX}/${getCultureToken(appCtx)}boats/${boatAlias}-${slugifyToURL(
      locationName
    )}-${boatId}-${locationId}${hasQueries}${currencyUrlPart}${hasDate}${hasPassengers}`;
  };

  return (
    <MapWrapper mapFullScreen={mapFullScreen} className={footerInView ? 'unfix' : ''}>
      <SearchMapWithClusters
        defaultCenter={{
          lat: mapCentre.lat,
          lng: mapCentre.lon,
        }}
        dragSearch={dragSearch}
        dragSearchVisible={dragSearchVisible}
        hoveredResultId={hoveredResultId}
        isInitialRender={isInitialRender.current}
        searchResults={boatsClusters}
        getBoatDetailUrl={getBoatDetailUrl}
        mapReallyVisible
        oldZoom={12}
        onLoad={(e) => setMap(e)}
        onDragEnd={onDragEnd}
        openMarker={openMarker}
        query={{
          action: 'search',
          lat: mapCentre.lat,
          lng: mapCentre.lon,
          ne_lat: router.query.latitudeNe,
          ne_lng: router.query.longitudeNe,
          sw_lat: router.query.latitudeSw,
          sw_lng: router.query.longitudeSw,
        }}
        dragButtonVisible={dragButtonVisible}
        setDragButtonVisible={setDragButtonVisible}
        setDragSearch={setDragSearch}
        setDragSearchVisible={setDragSearchVisible}
        setHoveredResultId={setHoveredResultId}
        setOpenMarker={setOpenMarker}
        setZoom={(z) => {
          setZoom(z);
        }}
        zoom={zoom}
        dragged={dragged}
        setDragged={setDragged}
      />
    </MapWrapper>
  );
}

const MapWrapper = styled.div`
  width: ${(props) => (props.mapFullScreen ? '100vw' : 'calc(40%)')};
  /* width: ${(props) => (props.mapFullScreen ? '100vw' : 'calc(100vw - 780px)')}; */
  min-width: 40vw;

  height: calc(100vh - 130px);
  overflow: hidden;
  position: sticky;
  top: 130px;
  right: 0;
  /* top: 129px; */

  &.unfix {
    /* position: absolute; */
    /* bottom: 50px; */
    /* top: auto; */
  }

  .slider.animated {
    z-index: 1;
  }

  .slide {
    background: transparent;
  }
  @media only screen and (max-width: 640px) {
    div[class*='SearchMapPanel'] {
      position: relative;
      top: 80px;
    }
  }
`;
